// AWS Authentication utility for dual authentication system

export enum AWSEnvironment {
  LOCAL = 'local',
  INTERNAL = 'internal',
  STAGING = 'staging',
  PRODUCTION = 'production'
}

export interface AWSAuthCredentials {
  email: string;
  password: string;
}

export interface AWSAuthResponse {
  token: string;
  user?: any;
  expiresAt?: number;
}

export interface AWSEnvironmentConfig {
  baseUrl: string;
  environment: AWSEnvironment;
}

// Environment detection based on query parameters
export const getAWSEnvironment = (): AWSEnvironmentConfig => {
  const urlParams = new URLSearchParams(window.location.search);
  const envParam = urlParams.get('env')?.toLowerCase();
  
  switch (envParam) {
    case AWSEnvironment.LOCAL:
      return {
        baseUrl: 'http://localhost:3001',
        environment: AWSEnvironment.LOCAL
      };
    
    case AWSEnvironment.INTERNAL:
      return {
        baseUrl: 'https://internal.core.guiderbackend.com',
        environment: AWSEnvironment.INTERNAL
      };
    
    case AWSEnvironment.STAGING:
      return {
        baseUrl: 'https://staging.core.guiderbackend.com',
        environment: AWSEnvironment.STAGING
      };
    
    case AWSEnvironment.PRODUCTION:
      return {
        baseUrl: 'https://production.core.guiderbackend.com',
        environment: AWSEnvironment.PRODUCTION
      };
    
    // Default to internal if no env parameter provided
    default:
      console.warn(`No env parameter provided, defaulting to internal. Use ?env=${Object.values(AWSEnvironment).join('|')}`);
      return {
        baseUrl: 'https://internal.core.guiderbackend.com',
        environment: AWSEnvironment.INTERNAL
      };
  }
};

// AWS Authentication Service
export class AWSAuthService {
  private static instance: AWSAuthService;
  private baseUrl!: string;
  private environment!: AWSEnvironment;
  private token: string | null = null;
  private tokenExpiresAt: number | null = null;

  private constructor() {
    this.loadEnvironmentFromStorage();
    this.loadTokenFromStorage();
  }

  private loadEnvironmentFromStorage(): void {
    // First check URL params (highest priority)
    const urlParams = new URLSearchParams(window.location.search);
    const envParam = urlParams.get('env')?.toLowerCase();
    
    if (envParam && Object.values(AWSEnvironment).includes(envParam as AWSEnvironment)) {
      // URL param exists and is valid - use it and save to storage
      const config = this.getConfigForEnvironment(envParam as AWSEnvironment);
      this.baseUrl = config.baseUrl;
      this.environment = config.environment;
      this.saveEnvironmentToStorage(config.environment);
      console.log(`🌍 Environment set from URL: ${config.environment}`);
      return;
    }
    
    // No URL param - try to restore from localStorage
    try {
      const storedEnv = localStorage.getItem('aws_environment');
      if (storedEnv && Object.values(AWSEnvironment).includes(storedEnv as AWSEnvironment)) {
        const config = this.getConfigForEnvironment(storedEnv as AWSEnvironment);
        this.baseUrl = config.baseUrl;
        this.environment = config.environment;
        console.log(`🌍 Environment restored from storage: ${config.environment}`);
        return;
      }
    } catch (error) {
      console.error('Error loading environment from storage:', error);
    }
    
    // Default to internal
    console.warn(`No env parameter provided, defaulting to internal. Use ?env=${Object.values(AWSEnvironment).join('|')}`);
    this.baseUrl = 'https://internal.core.guiderbackend.com';
    this.environment = AWSEnvironment.INTERNAL;
  }

  private getConfigForEnvironment(env: AWSEnvironment): AWSEnvironmentConfig {
    switch (env) {
      case AWSEnvironment.LOCAL:
        return { baseUrl: 'http://localhost:3001', environment: AWSEnvironment.LOCAL };
      case AWSEnvironment.INTERNAL:
        return { baseUrl: 'https://internal.core.guiderbackend.com', environment: AWSEnvironment.INTERNAL };
      case AWSEnvironment.STAGING:
        return { baseUrl: 'https://staging.core.guiderbackend.com', environment: AWSEnvironment.STAGING };
      case AWSEnvironment.PRODUCTION:
        return { baseUrl: 'https://production.core.guiderbackend.com', environment: AWSEnvironment.PRODUCTION };
    }
  }

  private saveEnvironmentToStorage(env: AWSEnvironment): void {
    try {
      localStorage.setItem('aws_environment', env);
    } catch (error) {
      console.error('Error saving environment to storage:', error);
    }
  }

  private updateEnvironment(): void {
    // Re-check URL for env param, but don't override stored env if URL has no param
    const urlParams = new URLSearchParams(window.location.search);
    const envParam = urlParams.get('env')?.toLowerCase();
    
    if (envParam && Object.values(AWSEnvironment).includes(envParam as AWSEnvironment)) {
      const config = this.getConfigForEnvironment(envParam as AWSEnvironment);
      this.baseUrl = config.baseUrl;
      this.environment = config.environment;
      this.saveEnvironmentToStorage(config.environment);
    }
    // If no URL param, keep the current/stored environment
  }

  public static getInstance(): AWSAuthService {
    if (!AWSAuthService.instance) {
      AWSAuthService.instance = new AWSAuthService();
    }
    return AWSAuthService.instance;
  }

  private loadTokenFromStorage(): void {
    try {
      const storedToken = localStorage.getItem('aws_jwt_token');
      const storedExpiry = localStorage.getItem('aws_jwt_expires_at');
      
      if (storedToken && storedExpiry) {
        const expiresAt = parseInt(storedExpiry, 10);
        if (Date.now() < expiresAt) {
          this.token = storedToken;
          this.tokenExpiresAt = expiresAt;
        } else {
          // Token expired, clear storage
          this.clearTokenFromStorage();
        }
      }
    } catch (error) {
      console.error('Error loading AWS token from storage:', error);
    }
  }

  private saveTokenToStorage(token: string, expiresAt?: number): void {
    try {
      localStorage.setItem('aws_jwt_token', token);
      if (expiresAt) {
        localStorage.setItem('aws_jwt_expires_at', expiresAt.toString());
      }
    } catch (error) {
      console.error('Error saving AWS token to storage:', error);
    }
  }

  private clearTokenFromStorage(): void {
    try {
      localStorage.removeItem('aws_jwt_token');
      localStorage.removeItem('aws_jwt_expires_at');
      this.token = null;
      this.tokenExpiresAt = null;
    } catch (error) {
      console.error('Error clearing AWS token from storage:', error);
    }
  }

  public async login(credentials: AWSAuthCredentials): Promise<AWSAuthResponse> {
    try {
      // Update environment in case URL parameters changed
      this.updateEnvironment();
      
      console.log(`🔐 Attempting AWS login to: ${this.baseUrl}/auth/login (${this.environment})`);
      
      const response = await fetch(`${this.baseUrl}/auth/login`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Authentication failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('🔍 AWS server response:', data);
      
      if (!data.token) {
        console.error('❌ Expected token field missing. Full response:', data);
        throw new Error(`No token received from AWS authentication. Server response: ${JSON.stringify(data)}`);
      }

      // Calculate expiry time (assuming JWT, typically 24 hours)
      const expiresAt = Date.now() + (24 * 60 * 60 * 1000); // 24 hours from now
      
      this.token = data.token;
      this.tokenExpiresAt = expiresAt;
      this.saveTokenToStorage(data.token, expiresAt);

      return {
        token: data.token,
        user: data.user,
        expiresAt
      };
    } catch (error) {
      console.error('AWS login error:', error);
      throw error;
    }
  }

  public logout(): void {
    this.clearTokenFromStorage();
  }

  public getToken(): string | null {
    if (this.token && this.tokenExpiresAt && Date.now() < this.tokenExpiresAt) {
      return this.token;
    }
    
    // Token expired or doesn't exist
    this.clearTokenFromStorage();
    return null;
  }

  public isAuthenticated(): boolean {
    return this.getToken() !== null;
  }

  public getAuthHeaders(): Record<string, string> {
    const token = this.getToken();
    if (!token) {
      throw new Error('No valid AWS token available');
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  public async makeAuthenticatedRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    // Update environment in case URL parameters changed
    this.updateEnvironment();
    
    const headers = {
      ...this.getAuthHeaders(),
      ...options.headers,
    };

    console.log(`🌐 Making AWS request to: ${this.baseUrl}${endpoint} (${this.environment})`);
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      mode: 'cors',
      headers,
    });

    // If we get a 401, the token might be expired
    if (response.status === 401) {
      this.clearTokenFromStorage();
      throw new Error('AWS authentication expired. Please log in again.');
    }

    return response;
  }

  public async callFunction(functionName: string, args: any = {}): Promise<any> {
    console.log(`🔧 Calling AWS function: ${functionName}`, args);
    
    const response = await this.makeAuthenticatedRequest('/callFunction', {
      method: 'POST',
      body: JSON.stringify({
        functionName,
        args
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || `Function call failed: ${response.status}`;
      console.error(`❌ AWS function call failed: ${functionName}`, errorMessage);
      throw new Error(errorMessage);
    }
    
    const result = await response.json();
    console.log(`✅ AWS function call successful: ${functionName}`, result);
    return result;
  }

  public getEnvironmentInfo(): AWSEnvironmentConfig {
    return {
      baseUrl: this.baseUrl,
      environment: this.environment
    };
  }
}

// Export singleton instance
export const awsAuthService = AWSAuthService.getInstance();
